import { useCallback, useMemo } from 'react';
import { Dispatch, SetStateAction } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Task } from 'utils/customTypes';
import { TaskFormFields } from 'utils/types/task';
import { TaskTemplate } from 'utils/types/taskTemplate';
import { useTaskBundleDraft } from '../../providers/TaskBundleProvider/TaskBundleProvider';
import TaskModal from 'Organisms/TaskModal/TaskModal';
import TabbedTaskTemplateModal from './TabbedTaskTemplateModal';

type TaskTemplateModalMode = 'create' | 'edit';

interface TaskTemplateModalProps {
  mode: TaskTemplateModalMode;
  isOpen: boolean;
  onClose: () => void;
  taskTemplateData?: TaskTemplate;
}

const buildTaskData = (template?: TaskTemplate): Task => ({
  id: template?.id ?? '',
  name: template?.name ?? '',
  description: template?.description,
  type: template?.type,
  estimate_hours: template?.estimated_hours,
  createdAt: '',
});

const TaskTemplateModal = ({
  mode,
  isOpen,
  onClose,
  taskTemplateData,
}: TaskTemplateModalProps) => {
  const {
    newTaskTypes = false,
    taskDependenciesInBundles = false,
    relativeDates = false,
  } = useFlags();
  const { actions, selectors } = useTaskBundleDraft();

  const isEditing = mode === 'edit';
  const isCreating = mode === 'create';

  const taskData = useMemo(
    () => buildTaskData(taskTemplateData),
    [taskTemplateData]
  );

  const handleSetIsOpen = useCallback<Dispatch<SetStateAction<boolean>>>(
    (value) => {
      if (value === false) {
        onClose();
      }
    },
    [onClose]
  );

  const handleSaveTask = useCallback(
    (taskFields: TaskFormFields) => {
      const existingDeps = selectors.getTemplateDependencies(
        taskTemplateData?.id ?? ''
      );
      const templateData = {
        name: taskFields.name,
        description: taskFields.description,
        estimated_hours: taskFields.estimate_hours,
        type: taskFields.type,
        dependencies: {
          predecessors: existingDeps?.predecessors ?? [],
          successors: existingDeps?.successors ?? [],
        },
      };
      if (isCreating) {
        actions.addTemplate({ ...templateData, id: uuidv4() });
      } else if (isEditing && taskTemplateData) {
        actions.updateTemplate(taskTemplateData.id, templateData);
      }
    },
    [taskTemplateData, actions, selectors, isCreating, isEditing]
  );

  if (newTaskTypes && (taskDependenciesInBundles || relativeDates)) {
    return (
      <TabbedTaskTemplateModal
        {...{ mode, isOpen, onClose, taskTemplateData }}
      />
    );
  }

  return (
    <TaskModal
      isOpen={isOpen}
      setIsOpen={handleSetIsOpen}
      taskData={taskData}
      isTaskTemplate
      isCentralizedTask={false}
      onSaveTask={handleSaveTask}
    />
  );
};

export default TaskTemplateModal;
